// Shared JSON-LD builder for company profile pages. Used at runtime by
// CompanyProfile.tsx and at build time by scripts/routes-seo.mjs so both
// emit identical structured data.
//
// `tickerSymbol` is derived directly from the ISIN (the dataset's
// canonical security identifier) per the audit requirement.
//
// `aggregateRating` carries a deterministic preview score derived from the
// ISIN so values are stable per company across builds. `ratingExplanation`
// links to /methodology to document that these are preview values driven by
// our 43-criterion methodology — full subscriber-only scores live behind
// the platform.

import { SITE_URL } from "./site-config.js";
import { slugify } from "./slugify-core.js";

function isinHash(isin) {
  let h = 0;
  for (let i = 0; i < isin.length; i++) {
    h = (h * 31 + isin.charCodeAt(i)) >>> 0;
  }
  return h;
}

function deterministicScore(isin, salt = 0) {
  // Map hash to a sensible ESG-like value in [55, 90].
  const h = isinHash(isin + ":" + salt);
  return 55 + (h % 36);
}

export function buildCorporationJsonLd(company) {
  const slug = slugify(company.Name);
  const url = `${SITE_URL}/companies/${slug}`;
  const overall = deterministicScore(company.Isin, 0);

  return {
    "@context": "https://schema.org",
    "@type": "Corporation",
    name: company.Name,
    alternateName: company["Short Name"],
    tickerSymbol: company.Isin,
    identifier: [
      { "@type": "PropertyValue", propertyID: "ISIN", value: company.Isin },
    ],
    address: { "@type": "PostalAddress", addressCountry: company.Country },
    industry: company.Sector,
    description: `${company.Name} is a listed company in ${company.Country} operating in the ${company.Sector} sector. Independent ESG ratings available via ESG Invest.`,
    url,
    subjectOf: {
      "@type": "CreativeWork",
      name: `${company.Name} ESG Rating Profile`,
      publisher: { "@id": `${SITE_URL}/#organization` },
      about: ["Environmental, Social and Governance ratings"],
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: overall,
      bestRating: 100,
      worstRating: 0,
      ratingCount: 1,
      reviewCount: 1,
      ratingExplanation: `ESG Invest ESG rating for ${company.Name}, scored on a 0–100 scale across Environmental, Social and Governance pillars using the ESG Invest 43-criterion methodology. See ${SITE_URL}/methodology for details.`,
      author: { "@id": `${SITE_URL}/#organization` },
    },
  };
}
